﻿import { FC, PropsWithChildren, useMemo } from "react";
import { AppInsightsContext, ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { useEnvironmentContext } from "../EnvironmentContext";
import { IApplication, IConfig } from "@microsoft/applicationinsights-common";
import { IConfiguration } from "@microsoft/applicationinsights-core-js";
import { useApiContext } from "../ApiContext";
import { parseJwt } from "../../util";

export type TelemetryContextProviderProps = PropsWithChildren<{
    oid?: string;
    appInsightsConfig?: IConfiguration & IConfig;
    version: IApplication;
    onAppInsightsInit?: (appInsights?: ApplicationInsights) => void;
}>;

export type OIDAwareAppInsightsContextProviderProps = Omit<TelemetryContextProviderProps, "oid">;

export const OIDAwareAppInsightsContextProvider: FC<OIDAwareAppInsightsContextProviderProps> = props => {
    const { idToken } = useApiContext();
    const oIdToken = parseJwt(idToken);
    return <AppInsightsContextProvider {...props} oid={oIdToken?.oid} />;
};

export const AppInsightsContextProvider: FC<TelemetryContextProviderProps> = ({ oid, appInsightsConfig, version, onAppInsightsInit, children }) => {
    const { aiConnectionString, aiCloudRole, aiCloudRoleInstance } = useEnvironmentContext();

    const aiReact = useMemo(() => {
        const reactPlugin = new ReactPlugin();

        const appInsights = new ApplicationInsights({
            config: {
                // @ts-ignore
                connectionString: aiConnectionString,
                enableAutoRouteTracking: false, //disabled because it was high-churn
                // @ts-ignore
                extensions: [reactPlugin],
                ...appInsightsConfig,
            },
        });

        appInsights.loadAppInsights();
        if (version) {
            appInsights.context.application.ver = version.ver;
            appInsights.context.application.build = version.build;
        }

        appInsights.addTelemetryInitializer(item => {
            if (item.tags) {
                item.tags["ai.cloud.role"] = aiCloudRole;
                item.tags["ai.cloud.roleInstance"] = aiCloudRoleInstance;
            }
        });

        if (oid) {
            appInsights.setAuthenticatedUserContext(oid, undefined, true);
        }

        if (onAppInsightsInit) {
            onAppInsightsInit(appInsights);
        }

        return reactPlugin;
    }, [aiConnectionString, appInsightsConfig, version, onAppInsightsInit, oid]);

    return <AppInsightsContext.Provider value={aiReact}>{children}</AppInsightsContext.Provider>;
};
